import React, { useEffect, useState } from 'react';
import Card from '@components-clean/core/card';
import useGridCustomCell from '@components-clean/core/grid/custom-cell';
import Grid from '@components-clean/core/grid-fe';
import { DateCell } from '@components-clean/core/grid-fe/custom-cells';
import { GridColumnProps } from '@components-clean/core/grid-fe/grid.interface';
import Loading from '@components-clean/core/loading/loading';
import { StudyReviewDto } from '@interfaces/central-monitoring';
import { useLocalState, useTranslation } from '@hooks/index';
import { AuditApplicationArea, AuditDataType, AuditServiceType } from '@types/graphql-global-types';
import DatasetReviewsApi from '@apis/bff-api/central-monitoring/dataset-reviews';

interface IProps {
    studyRef: string;
}

export type FormattedStudyReviews = {
    reviewedStatus: string;
} & StudyReviewDto;

const ReviewHistoryGrid = ({ studyRef }: IProps) => {
    const { t } = useTranslation();
    const [datasetReviews, setDatasetReviews] = useState<FormattedStudyReviews[]>([]);
    const { data, isLoading, isValidating } = DatasetReviewsApi.useGetAll(studyRef);
    const { TooltipCell } = useGridCustomCell();
    const { state: localState } = useLocalState();
    const studyName = localState?.selectedStudy?.studyNumber;
    const datasetDate = localState?.selectedCM2Dataset?.dataCutDate;

    const isFetching = isLoading || isValidating;
    const reviewedStatusKeys = ['reviewed', 'unreviewed'];

    useEffect(() => {
        const formattedDatasets = data?.map((studyReview: StudyReviewDto) => ({
            ...studyReview,
            reviewedStatus: studyReview.reviewed ? t('cm:reviewed') : t('cm:unreviewed'),
        }));
        setDatasetReviews(formattedDatasets ?? []);
    }, [data]);

    const DataSetReviewsColumns: GridColumnProps[] = [
        {
            id: 'datasetDate',
            field: 'datasetDate',
            title: 'Dataset Date',
            width: 1,
            minWidth: 140,
            locked: true,
            filter: 'date',
            cells: { data: DateCell },
            cellName: 'DateCell',
        },
        {
            id: 'reviewed',
            field: 'reviewedStatus',
            title: 'Review Status',
            width: 1,
            minWidth: 120,
            filter: 'text',
            locked: true,
            lovData: reviewedStatusKeys.map((key) => t(`cm:${key}`)),
        },
        {
            id: 'summary',
            field: 'summary',
            title: 'Review Summary',
            cells: { data: TooltipCell() },
            width: 1,
            minWidth: 200,
            filter: 'text',
            locked: false,
        },
        {
            id: 'startDate',
            field: 'startDate',
            title: 'Reviewed Start Date',
            width: 1,
            minWidth: 140,
            locked: false,
            filter: 'date',
            cells: { data: DateCell },
            cellName: 'DateCell',
        },
        {
            id: 'completedDate',
            field: 'completedDate',
            title: 'Reviewed End Date',
            width: 1,
            minWidth: 140,
            locked: false,
            filter: 'date',
            cells: { data: DateCell },
            cellName: 'DateCell',
        },
        {
            id: 'reviewer',
            field: 'reviewer',
            title: 'Reviewer',
            minWidth: 140,
            width: 1,
            filter: 'text',
            locked: false,
        },
    ];

    if (!datasetReviews || isLoading) {
        return (
            <Card className="loading-card" data-testid="loading-card">
                <Loading />
            </Card>
        );
    }

    return (
        <Grid
            title="Review History"
            maxHeight="auto"
            testDataId="study-dataset-review-grid"
            data={datasetReviews}
            columns={DataSetReviewsColumns}
            loading={isFetching}
            useLoadingOverlay
            exportFilename={`${studyName}_${datasetDate}_Study_Dataset_Review_History`}
            showAddIcon={false}
            service={AuditServiceType.CENTRAL_MONITORING_2}
            area={AuditApplicationArea.CENTRAL_MONITORING_2}
            dataType={AuditDataType.EXCEL_EXPORT}
            auditDescriptionPrefix="CM - Toolkit - Study Dataset Review History"
        />
    );
};

export default ReviewHistoryGrid;
